// ** REGEX CONSTANTS
import { ALPHA_NUMERIC_REGEX, PASSWORD_REGEX } from "./regex";
import { array, mixed, number, string } from "yup";

import size from "lodash/size";
import { useTranslation } from "react-i18next";
import { validateEmailTLD } from "@helpers/utils";

const Validations = () => {
    const { t } = useTranslation();
    /**
   * Validation For Required Strings
   * @param name
   */
    const requiredValidation = ({ name }) => string()
        .required(`${t(name)} ${t("is required")}`)
        .nonNullable(`${t(name)} ${t("is required")}`);

    const numberValidation = ({ name, min = 0, max = Infinity }) => number()
        .required(`${t(name)} ${t("is required")}`)
        .typeError(`${t(name)} ${t("is required")}`)
        .positive(`${t(name)} ${t("must be a positive number")}`)
        .min(
            min,
            `${t(name)} ${t("must be greater than or equal to {{min}}", { min })}`
        )
        .max(
            max,
            `${t(name)} ${t("must be less than or equal to {{max}}", { max })}`
        )
        .integer(`${t(name)} ${t("must be a integer value only")}`);

    /**
   * Validation For Required email
   * @param name
   */
    const emailRequiredValidation = ({ name }) => requiredValidation({ name })
        .email(`${t(name)} ${t("must be valid")}`)
        .max(350, `${t(name)} ${t("must be atmost 350 characters")}`)
        .test("is-valid", `${t(name)} ${t("must be valid")}`, (value) => validateEmailTLD(value)
        );
    /**
   * Validation For email
   * @param name
   */
    const emailValidation = ({ name }) => string()
        .email(`${t(name)} ${t("must be valid")}`)
        .max(350, `${t(name)} ${t("must be atmost 350 characters")}`)
        .test("is-valid", `${t(name)} ${t("must be valid")}`, (value) => validateEmailTLD(value)
        );

    const emailCommonValidation = ({ name, required = false }) => {
        const base = (required ? requiredValidation({ name }) : string())
            .email(`${t(name)} ${t("must be valid")}`)
            .max(350, `${t(name)} ${t("must be atmost 350 characters")}`)
            .test("is-valid", `${t(name)} ${t("must be valid")}`, (value) => validateEmailTLD(value));

        return base;
    };

    /**
   * Optional Token Validation With Minimum Length
   * @param name
   * @param min
   * @param msg
   */
    const validateIfPresent = ({
        name,
        min = 60,
        max = Infinity,
        regex = { regex: ALPHA_NUMERIC_REGEX },
        msg = "Invalid Value"
    }) => string()
        .trim()
        .nullable()
        .transform((value, originalValue) => (originalValue === "" ? null : value))
        .test(
            "min-if-present",
            `${t(name)} ${t("must be longer than or equal to {{min}} characters", { min })}`,
            (value) => !value || value.length >= min
        )
        .test(
            "max-if-present",
            `${t(name)} ${t("must be shorter than or equal to {{max}} characters", { max })}`,
            (value) => !value || !max || value.length <= max
        )
        .matches(regex.pattern, t(regex.msg) || t(msg));
      
    /**
   * Validation For Min Max String
   * @param name
   * @param min
   * @param max
   */
    const minMaxString = ({ name, min = 3, max = 32 }) => string()
        .nullable()
        .transform((o, c) => (o === "" ? null : c))
        .min(min, `${t(name)} ${t("must be atleast")} ${min} ${t("characters")}`)
        .max(max, `${t(name)} ${t("must be atmost")} ${max} ${t("characters")}`);
    /**
   * Validation For Min Max Required String
   * @param name
   * @param min
   * @param max
   */
    const minMaxRequiredString = ({ name, min = 3, max = 32 }) => requiredValidation({ name })
        .min(min, `${t(name)} ${t("must be atleast")} ${min} ${t("characters")}`)
        .max(max, `${t(name)} ${t("must be atmost")} ${max} ${t("characters")}`);

    //===================
    //All Regex Related Validations
    //==================

    /**
   * Required REGEX _MATCH
   * @param name
   * @param regex
   */
    const requiredRegexValidation = ({ name, regex }) => requiredValidation({ name }).matches(
        regex.pattern,
        t(regex.msg) || t("Invalid Value")
    );
    const emptyObjValidation = ({ name }) => mixed()
        .test("is-not-empty", `${name} ${t("is required")}`, function (value) {
            return size(value) > 0;
        });

    /**
   * This is a function that uses regular expressions to validate a string input and returns an error
   * message if the input does not match the specified regex pattern.
   */
    const regexValidation = ({ regex }) => {
        return  string()
            .trim()
            .matches(regex.pattern, t(regex.msg) || t("Invalid Value"))
            .nullable();
    };
    /**
   * Validation For Min Max Alphanumeric Required Strings
   * @param name
   * @param min
   * @param max
   * @param regex
   */
    const minMaxRegexRequired = ({
        name,
        min = 3,
        max = 32,
        regex = { regex: ALPHA_NUMERIC_REGEX },
        msg = "Invalid Value"
    }) => requiredValidation({ name })
        .min(min, `${t(name)} ${t("must be atleast")} ${min} ${t("characters")}`)
        .max(max, `${t(name)} ${t("must be atmost")} ${max} ${t("characters")}`)
        .matches(regex.pattern, t(regex.msg) || t(msg));

    /**
   * This is a validation function that checks if an array has a minimum length and returns an error
   * message if it doesn't.
   */
    const arrayMinMaxLengthValidation = ({
        name = "",
        min,
        max = Infinity, // Default to positive infinity if max is not provided
        msgMin = `${t("At least")} ${min} ${name} ${t("is required")}`,
        msgMax = `${t("At most")} ${max} ${name} ${t("is allowed")}`
    }) => array().min(min, msgMin).max(max, msgMax);

    /**
   * Validation For Conditional Password (Old and New Password)
   * @param name
   * @param compareField
   * @param min
   * @param max
   * @param regex
   **/
    const conditionalPassword = ({
        name,
        compareField,
        min = 8,
        max = 32,
        regex = PASSWORD_REGEX
    }) => string().when(compareField, {
        is  : (value) => value,
        then: () => (compareField !== "oldPassword" ? minMaxRegexRequired({ name, min, max, regex }) : minMaxRegexRequired({ name, regex, min, max }))
    });

    /**
   * The function `arrayElementsValidation` validates that a given value is one of the elements in an
   * array.
   */
    const arrayElementsValidation = ({ arr, name }) => string()
        .oneOf(
            arr.map((item) => item.value).concat([""]),
            `${name} must be one of the following values: ${arr
                .map((item) => item.label)
                .join(", ")}`
        );

    /**
 * Validation to ensure min is always less than max
 * @param {string} _minField - key for the min field (kept for clarity, unused directly)
 * @param {string} maxField - key for the max field
 * @param {string} label - label for error message context
 */
    const minLessThanMaxValidation = ({ maxField, label }) => number()
        .nullable()
        .typeError(`${t(label)} must be a number`)
        .when(maxField, (maxValue, schema) => {
            if (maxValue !== null) {
                return schema.test(
                    "min-less-than-max",
                    t("Minimum value must be less than maximum value"),
                    (minValue) => minValue === null || Number(minValue) < Number(maxValue)
                );
            }
            return schema;
        });

    return {
        arrayMinMaxLengthValidation,
        minMaxRegexRequired,
        regexValidation,
        requiredRegexValidation,
        conditionalPassword,
        minMaxRequiredString,
        minMaxString,
        emailRequiredValidation,
        numberValidation,
        requiredValidation,
        emailValidation,
        emptyObjValidation,
        arrayElementsValidation,
        validateIfPresent,
        emailCommonValidation,
        minLessThanMaxValidation
    };
};

export default Validations;
